import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { humanize } from '@/modules/simulator/ui/text';
import type { TicketBreakdownLevel, TicketBreakdownRow } from '@/types/analyticsV2';

const LEVEL_LABEL: Record<TicketBreakdownLevel, string> = {
  category: 'Category',
  subcategory: 'Subcategory',
  intent: 'Intent',
};

const rowName = (by: TicketBreakdownLevel, { key }: TicketBreakdownRow): string => {
  if (key === null) return 'Uncategorized';
  return by === 'intent' ? humanize(key) : key;
};

export const canDrill = (by: TicketBreakdownLevel, row: TicketBreakdownRow): boolean =>
  by !== 'intent' && row.key !== null;

const countPercent = ({ count, percent }: { count: number; percent: number | null }) => (
  <div className='flex items-baseline gap-2'>
    <span>{count}</span>
    {percent !== null ? <span className='text-gray-700 text-[12px]'>{percent.toFixed(0)} %</span> : null}
  </div>
);

export const breakdownColumns = (by: TicketBreakdownLevel): ColumnDef<TicketBreakdownRow>[] => [
  {
    id: 'name',
    header: LEVEL_LABEL[by],
    size: 260,
    cell: ({ row }) => {
      const name = (
        <>
          <div className='h-4 w-[2px] rounded-full bg-gray-500' />
          <span className='text-ink truncate'>{rowName(by, row.original)}</span>
        </>
      );
      return canDrill(by, row.original) ? (
        <button type='button' className='flex max-w-full items-center gap-2 text-left'>
          {name}
          <ChevronRight className='size-3.5 shrink-0 text-gray-500' />
        </button>
      ) : (
        <div className='flex items-center gap-2'>{name}</div>
      );
    },
  },
  { accessorKey: 'tickets', header: 'Tickets', size: 110 },
  { id: 'aiHandled', header: 'AI handled', size: 140, cell: ({ row }) => countPercent(row.original.aiHandled) },
  { id: 'automated', header: 'Automated', size: 140, cell: ({ row }) => countPercent(row.original.automated) },
  {
    id: 'avgAiResponses',
    header: 'AI responses /ticket',
    size: 160,
    cell: ({ row }) => row.original.avgAiResponses?.toFixed(2) ?? '—',
  },
];
